import React from 'react';
import AdminNavbar from "../../Navbars/AdminNavbar.js";
import AdminSidebar from '../../Sidebar/AdminSidebar';
import { ApiGatewayApis } from '../../../apis/ApiGatewayApis';
import { useNavigate } from 'react-router-dom';
import { Oval } from 'react-loader-spinner';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    ArrowLeft, Search, Eye, CheckCircle, XCircle, Ban,
    ChevronLeft, ChevronRight, X, UserPlus, Clock,
    Building2, Mail, Globe, FileText, Copy, Key,
} from 'lucide-react';

function PortalAccountManagement() {
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState<boolean>(true);
    const [accounts, setAccounts] = React.useState<any[]>([]);
    const [meta, setMeta] = React.useState<any>(null);
    const [page, setPage] = React.useState<number>(1);
    const [search, setSearch] = React.useState<string>('');
    const [statusFilter, setStatusFilter] = React.useState<string>('');
    const [typeFilter, setTypeFilter] = React.useState<string>('');

    // Modals
    const [showApproveModal, setShowApproveModal] = React.useState<any>(null);
    const [showRejectModal, setShowRejectModal] = React.useState<any>(null);
    const [showKeyModal, setShowKeyModal] = React.useState<any>(null);
    const [actionLoader, setActionLoader] = React.useState<boolean>(false);

    // Approve form
    const [approveForm, setApproveForm] = React.useState({ rate_limit_per_minute: 120 });
    // Reject reason
    const [rejectReason, setRejectReason] = React.useState<string>('');

    // Summary counts
    const [counts, setCounts] = React.useState({ total: 0, pending: 0, active: 0, rejected: 0 });

    React.useEffect(() => { loadAccounts(); }, [page, statusFilter, typeFilter]);

    const loadAccounts = () => {
        setLoading(true);
        ApiGatewayApis.getAllPortalAccounts(page, 20, {
            search,
            status: statusFilter,
            account_type: typeFilter,
        })
            .then((response: any) => {
                setLoading(false);
                if (response?.data) {
                    const data = response.data.data || response.data;
                    if (Array.isArray(data?.data)) {
                        // Paginated response from Laravel
                        setAccounts(data.data);
                        setMeta({
                            current_page: data.current_page,
                            last_page: data.last_page,
                            per_page: data.per_page,
                            total: data.total,
                        });
                    } else if (Array.isArray(data)) {
                        setAccounts(data);
                        setMeta(null);
                    }

                    // Calculate summary counts from all data or response
                    const all = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
                    // If server provides total in meta, use that
                    if (data?.total) {
                        setCounts(prev => ({ ...prev, total: data.total }));
                    }
                }
            })
            .catch((error: any) => {
                setLoading(false);
                toast.error('Failed to load portal accounts');
            });
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(1);
        loadAccounts();
    };

    const handleApprove = (e: React.FormEvent) => {
        e.preventDefault();
        if (!showApproveModal) return;
        setActionLoader(true);

        ApiGatewayApis.approvePortalAccount(showApproveModal.id, {
            rate_limit_per_minute: approveForm.rate_limit_per_minute,
        })
            .then((response: any) => {
                setActionLoader(false);
                if (response?.data?.success) {
                    toast.success('Account approved! API key generated and sent via email.');
                    setShowApproveModal(null);
                    setApproveForm({ rate_limit_per_minute: 120 });

                    // Show the generated API key to admin
                    const apiKey = response.data.data?.api_key;
                    const apiSecret = response.data.data?.api_secret;
                    if (apiKey) {
                        setShowKeyModal({ key: apiKey, secret: apiSecret, company: showApproveModal.company_name });
                    }

                    loadAccounts();
                } else {
                    toast.warn(response?.data?.message || 'Failed to approve');
                }
            })
            .catch((error: any) => {
                setActionLoader(false);
                toast.error(error?.response?.data?.message || 'Error approving account');
            });
    };

    const handleReject = (e: React.FormEvent) => {
        e.preventDefault();
        if (!showRejectModal || !rejectReason.trim()) return;
        setActionLoader(true);

        ApiGatewayApis.rejectPortalAccount(showRejectModal.id, { reason: rejectReason })
            .then((response: any) => {
                setActionLoader(false);
                if (response?.data?.success) {
                    toast.success('Account rejected. Notification sent.');
                    setShowRejectModal(null);
                    setRejectReason('');
                    loadAccounts();
                } else {
                    toast.warn(response?.data?.message || 'Failed to reject');
                }
            })
            .catch((error: any) => {
                setActionLoader(false);
                toast.error(error?.response?.data?.message || 'Error rejecting account');
            });
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-800';
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'rejected': return 'bg-red-100 text-red-800';
            case 'suspended': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-600';
        }
    };

    const getTypeBadge = (type: string) => {
        return type === 'partner'
            ? 'bg-blue-100 text-blue-800'
            : 'bg-purple-100 text-purple-800';
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-NG', {
            year: 'numeric', month: 'short', day: 'numeric',
        });
    };

    const pendingCount = accounts.filter(a => a.status === 'pending').length;

    return (
        <>
            <AdminSidebar title="Portal Accounts" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="Portal Account Management" />

                <div className='lg:px-10 pb-6 px-6 pt-5 bg-[#FAFAFA] min-h-screen'>

                    {/* Back + Header */}
                    <div className='flex items-center gap-3 mb-2'>
                        <button onClick={() => navigate('/admin/api-gateway')} className='text-gray-500 hover:text-[#E04343]'>
                            <ArrowLeft size={20} />
                        </button>
                        <h1 className='text-[20px] font-bold text-[#263238]'>Portal Account Management</h1>
                    </div>
                    <p className='text-[13px] text-[#808080] mb-5'>
                        Review and manage self-service portal registration requests from partners and customers.
                    </p>

                    {/* Summary Cards */}
                    <div className='grid grid-cols-2 md:grid-cols-4 gap-3 mb-5'>
                        {[
                            { label: 'Total', value: meta?.total || accounts.length, color: 'border-gray-400', icon: <Building2 size={16} className='text-gray-500' /> },
                            { label: 'Pending Review', value: accounts.filter(a => a.status === 'pending').length, color: 'border-yellow-500', icon: <Clock size={16} className='text-yellow-600' /> },
                            { label: 'Active', value: accounts.filter(a => a.status === 'active').length, color: 'border-green-500', icon: <CheckCircle size={16} className='text-green-600' /> },
                            { label: 'Rejected', value: accounts.filter(a => a.status === 'rejected').length, color: 'border-red-500', icon: <XCircle size={16} className='text-red-600' /> },
                        ].map((s) => (
                            <div key={s.label} className={`bg-white shadow-sm rounded-lg px-4 py-3 border-l-[3px] ${s.color}`}>
                                <div className='flex items-center justify-between'>
                                    <h5 className='text-[11px] text-[#808080]'>{s.label}</h5>
                                    {s.icon}
                                </div>
                                <h3 className='text-[20px] font-bold'>{s.value || 0}</h3>
                            </div>
                        ))}
                    </div>

                    {/* Search Bar + Filters */}
                    <div className='bg-white shadow-sm rounded-[10px] p-4 mb-4'>
                        <div className='flex flex-col md:flex-row gap-3 justify-between'>
                            <form onSubmit={handleSearch} className='flex gap-2 flex-1'>
                                <div className='relative flex-1 max-w-[400px]'>
                                    <Search size={16} className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400' />
                                    <input
                                        type='text'
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder='Search by company, name, or email...'
                                        className='bg-white border border-[#DADADA] text-sm rounded-[5px] pl-9 pr-3 py-2 w-full'
                                    />
                                </div>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                                    className='bg-white border border-[#DADADA] text-sm rounded-[5px] px-3 py-2'
                                >
                                    <option value=''>All Statuses</option>
                                    <option value='pending'>Pending</option>
                                    <option value='active'>Active</option>
                                    <option value='rejected'>Rejected</option>
                                    <option value='suspended'>Suspended</option>
                                </select>
                                <select
                                    value={typeFilter}
                                    onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
                                    className='bg-white border border-[#DADADA] text-sm rounded-[5px] px-3 py-2'
                                >
                                    <option value=''>All Types</option>
                                    <option value='partner'>Partners</option>
                                    <option value='customer'>Customers</option>
                                </select>
                                <button type='submit' className='bg-[#E04343] text-white px-4 py-2 rounded-[5px] text-sm hover:bg-[#C93636]'>
                                    Search
                                </button>
                            </form>
                        </div>
                    </div>

                    {/* Table */}
                    <div className='bg-white shadow-sm rounded-[10px] overflow-hidden'>
                        {loading ? (
                            <div className='flex justify-center py-16'>
                                <Oval visible={true} height="35" width="35" color="#E04343" secondaryColor="#FCBF94" />
                            </div>
                        ) : accounts.length === 0 ? (
                            <div className='text-center py-16'>
                                <UserPlus size={40} className='mx-auto text-gray-300 mb-3' />
                                <p className='text-[#808080] text-sm'>No portal account registrations found</p>
                            </div>
                        ) : (
                            <>
                                <div className='overflow-x-auto'>
                                    <table className='w-full text-left'>
                                        <thead>
                                            <tr className='bg-gray-50 border-b border-gray-200'>
                                                <th className='text-[11px] font-semibold text-[#808080] uppercase px-4 py-3'>Company</th>
                                                <th className='text-[11px] font-semibold text-[#808080] uppercase px-4 py-3'>Contact</th>
                                                <th className='text-[11px] font-semibold text-[#808080] uppercase px-4 py-3'>Type</th>
                                                <th className='text-[11px] font-semibold text-[#808080] uppercase px-4 py-3'>Status</th>
                                                <th className='text-[11px] font-semibold text-[#808080] uppercase px-4 py-3'>Date Applied</th>
                                                <th className='text-[11px] font-semibold text-[#808080] uppercase px-4 py-3 text-right'>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {accounts.map((account: any, idx: number) => (
                                                <tr key={account.id} className={`border-b border-gray-100 hover:bg-gray-50 transition ${account.status === 'pending' ? 'bg-yellow-50/30' : ''}`}>
                                                    <td className='px-4 py-3'>
                                                        <div className='flex items-center gap-2'>
                                                            <div className='w-8 h-8 rounded-full bg-[#E04343]/10 flex items-center justify-center'>
                                                                <Building2 size={14} className='text-[#E04343]' />
                                                            </div>
                                                            <div>
                                                                <p className='text-[13px] font-medium text-[#263238]'>{account.company_name}</p>
                                                                {account.website && (
                                                                    <p className='text-[11px] text-[#808080] flex items-center gap-1'>
                                                                        <Globe size={10} /> {account.website}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className='px-4 py-3'>
                                                        <p className='text-[13px] text-[#263238]'>{account.contact_name}</p>
                                                        <p className='text-[11px] text-[#808080] flex items-center gap-1'>
                                                            <Mail size={10} /> {account.contact_email}
                                                        </p>
                                                    </td>
                                                    <td className='px-4 py-3'>
                                                        <span className={`text-[11px] font-semibold px-2 py-[3px] rounded-full capitalize ${getTypeBadge(account.account_type)}`}>
                                                            {account.account_type}
                                                        </span>
                                                    </td>
                                                    <td className='px-4 py-3'>
                                                        <span className={`text-[11px] font-semibold px-2 py-[3px] rounded-full capitalize ${getStatusBadge(account.status)}`}>
                                                            {account.status}
                                                        </span>
                                                    </td>
                                                    <td className='px-4 py-3'>
                                                        <p className='text-[13px] text-[#808080]'>{formatDate(account.created_at)}</p>
                                                    </td>
                                                    <td className='px-4 py-3'>
                                                        <div className='flex items-center gap-1 justify-end'>
                                                            <button
                                                                onClick={() => navigate(`/admin/api-gateway/portal-accounts/${account.id}`)}
                                                                className='p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-[#E04343]'
                                                                title='View Details'
                                                            >
                                                                <Eye size={16} />
                                                            </button>

                                                            {account.status === 'pending' && (
                                                                <>
                                                                    <button
                                                                        onClick={() => setShowApproveModal(account)}
                                                                        className='p-1.5 rounded hover:bg-green-100 text-gray-500 hover:text-green-700'
                                                                        title='Approve'
                                                                    >
                                                                        <CheckCircle size={16} />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => setShowRejectModal(account)}
                                                                        className='p-1.5 rounded hover:bg-red-100 text-gray-500 hover:text-red-700'
                                                                        title='Reject'
                                                                    >
                                                                        <XCircle size={16} />
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {meta && meta.last_page > 1 && (
                                    <div className='flex items-center justify-between px-4 py-3 border-t border-gray-100'>
                                        <p className='text-[12px] text-[#808080]'>
                                            Page {meta.current_page} of {meta.last_page} · {meta.total} total
                                        </p>
                                        <div className='flex gap-1'>
                                            <button
                                                onClick={() => setPage(Math.max(1, page - 1))}
                                                disabled={page <= 1}
                                                className='p-1.5 rounded hover:bg-gray-100 disabled:opacity-30'
                                            >
                                                <ChevronLeft size={16} />
                                            </button>
                                            <button
                                                onClick={() => setPage(Math.min(meta.last_page, page + 1))}
                                                disabled={page >= meta.last_page}
                                                className='p-1.5 rounded hover:bg-gray-100 disabled:opacity-30'
                                            >
                                                <ChevronRight size={16} />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* ========== APPROVE MODAL ========== */}
            {showApproveModal && (
                <div className='fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4'>
                    <div className='bg-white rounded-[10px] w-full max-w-[480px] shadow-xl'>
                        <div className='flex justify-between items-center px-5 py-4 border-b border-gray-100'>
                            <h3 className='text-[15px] font-semibold text-[#263238] flex items-center gap-2'>
                                <CheckCircle size={18} className='text-green-600' />
                                Approve Account
                            </h3>
                            <button onClick={() => setShowApproveModal(null)} className='text-gray-400 hover:text-gray-600'>
                                <X size={18} />
                            </button>
                        </div>
                        <form onSubmit={handleApprove}>
                            <div className='p-5 space-y-4'>
                                {/* Account Summary */}
                                <div className='bg-green-50 rounded-lg p-3 border border-green-100'>
                                    <p className='text-[13px] text-green-800'>
                                        Approving <strong>{showApproveModal.company_name}</strong> ({showApproveModal.account_type})
                                    </p>
                                    <p className='text-[11px] text-green-700 mt-1'>
                                        {showApproveModal.contact_name} · {showApproveModal.contact_email}
                                    </p>
                                </div>

                                <p className='text-[12px] text-[#808080]'>
                                    This will create a Partner record, generate an API key pair, and send the credentials to the applicant via email.
                                </p>

                                {/* Rate limit */}
                                <div>
                                    <label className='text-[12px] font-medium text-[#263238] block mb-1'>Rate Limit (requests/minute)</label>
                                    <input
                                        type='number'
                                        value={approveForm.rate_limit_per_minute}
                                        onChange={(e) => setApproveForm({ ...approveForm, rate_limit_per_minute: parseInt(e.target.value) || 120 })}
                                        min={10}
                                        max={10000}
                                        className='bg-white border border-[#DADADA] text-sm rounded-[5px] px-3 py-2 w-full'
                                    />
                                </div>
                            </div>
                            <div className='px-5 py-3 border-t border-gray-100 flex gap-2 justify-end'>
                                <button type='button' onClick={() => setShowApproveModal(null)} className='px-4 py-2 text-sm border border-[#DADADA] rounded-[5px] hover:bg-gray-50'>
                                    Cancel
                                </button>
                                <button
                                    type='submit'
                                    disabled={actionLoader}
                                    className='px-4 py-2 text-sm bg-green-600 text-white rounded-[5px] hover:bg-green-700 disabled:opacity-50 flex items-center gap-2'
                                >
                                    {actionLoader && <Oval visible={true} height="14" width="14" color="#fff" secondaryColor="#fff" />}
                                    Approve & Generate Key
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ========== REJECT MODAL ========== */}
            {showRejectModal && (
                <div className='fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4'>
                    <div className='bg-white rounded-[10px] w-full max-w-[480px] shadow-xl'>
                        <div className='flex justify-between items-center px-5 py-4 border-b border-gray-100'>
                            <h3 className='text-[15px] font-semibold text-[#263238] flex items-center gap-2'>
                                <XCircle size={18} className='text-red-600' />
                                Reject Application
                            </h3>
                            <button onClick={() => setShowRejectModal(null)} className='text-gray-400 hover:text-gray-600'>
                                <X size={18} />
                            </button>
                        </div>
                        <form onSubmit={handleReject}>
                            <div className='p-5 space-y-4'>
                                <div className='bg-red-50 rounded-lg p-3 border border-red-100'>
                                    <p className='text-[13px] text-red-800'>
                                        Rejecting <strong>{showRejectModal.company_name}</strong>
                                    </p>
                                    <p className='text-[11px] text-red-700 mt-1'>
                                        {showRejectModal.contact_name} · {showRejectModal.contact_email}
                                    </p>
                                </div>

                                <div>
                                    <label className='text-[12px] font-medium text-[#263238] block mb-1'>Rejection Reason <span className='text-red-500'>*</span></label>
                                    <textarea
                                        value={rejectReason}
                                        onChange={(e) => setRejectReason(e.target.value)}
                                        placeholder='Provide a reason for rejection (will be sent to the applicant)...'
                                        rows={3}
                                        required
                                        className='bg-white border border-[#DADADA] text-sm rounded-[5px] px-3 py-2 w-full resize-none'
                                    />
                                </div>
                            </div>
                            <div className='px-5 py-3 border-t border-gray-100 flex gap-2 justify-end'>
                                <button type='button' onClick={() => setShowRejectModal(null)} className='px-4 py-2 text-sm border border-[#DADADA] rounded-[5px] hover:bg-gray-50'>
                                    Cancel
                                </button>
                                <button
                                    type='submit'
                                    disabled={actionLoader || !rejectReason.trim()}
                                    className='px-4 py-2 text-sm bg-red-600 text-white rounded-[5px] hover:bg-red-700 disabled:opacity-50 flex items-center gap-2'
                                >
                                    {actionLoader && <Oval visible={true} height="14" width="14" color="#fff" secondaryColor="#fff" />}
                                    Reject & Notify
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ========== API KEY REVEAL MODAL ========== */}
            {showKeyModal && (
                <div className='fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4'>
                    <div className='bg-white rounded-[10px] w-full max-w-[520px] shadow-xl'>
                        <div className='flex justify-between items-center px-5 py-4 border-b border-gray-100'>
                            <h3 className='text-[15px] font-semibold text-[#263238] flex items-center gap-2'>
                                <Key size={18} className='text-[#E04343]' />
                                Generated API Credentials
                            </h3>
                            <button onClick={() => setShowKeyModal(null)} className='text-gray-400 hover:text-gray-600'>
                                <X size={18} />
                            </button>
                        </div>
                        <div className='p-5 space-y-4'>
                            <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-3'>
                                <p className='text-[12px] text-yellow-800 font-medium'>
                                    These credentials are shown ONCE and have been sent to {showKeyModal.company}'s email.
                                    Copy them now if needed.
                                </p>
                            </div>

                            {/* API Key */}
                            <div>
                                <label className='text-[11px] font-semibold text-[#808080] uppercase block mb-1'>API Key</label>
                                <div className='flex items-center gap-2'>
                                    <code className='flex-1 bg-[#1a1a2e] text-[#4ade80] px-3 py-2 rounded text-[12px] font-mono break-all'>
                                        {showKeyModal.key}
                                    </code>
                                    <button onClick={() => copyToClipboard(showKeyModal.key)} className='p-2 hover:bg-gray-100 rounded' title='Copy'>
                                        <Copy size={16} className='text-gray-500' />
                                    </button>
                                </div>
                            </div>

                            {/* API Secret */}
                            {showKeyModal.secret && (
                                <div>
                                    <label className='text-[11px] font-semibold text-[#808080] uppercase block mb-1'>API Secret</label>
                                    <div className='flex items-center gap-2'>
                                        <code className='flex-1 bg-[#1a1a2e] text-[#60a5fa] px-3 py-2 rounded text-[12px] font-mono break-all'>
                                            {showKeyModal.secret}
                                        </code>
                                        <button onClick={() => copyToClipboard(showKeyModal.secret)} className='p-2 hover:bg-gray-100 rounded' title='Copy'>
                                            <Copy size={16} className='text-gray-500' />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className='px-5 py-3 border-t border-gray-100 flex justify-end'>
                            <button onClick={() => setShowKeyModal(null)} className='px-4 py-2 text-sm bg-[#E04343] text-white rounded-[5px] hover:bg-[#C93636]'>
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
        </>
    );
}

export default PortalAccountManagement;